import dayjs from 'dayjs'
import { openConfirmModal, Tag } from '@gate/gui'
import { Flex } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import useRateChange from '@/hooks/useRateChange'
import BaseImage from '@/components/BaseImage'
import BaseEmpty from '@/components/BaseEmpty'
import { useTranslation } from 'next-i18next'
import Action from '@/blocks/Price/PriceTable/Columns/Action'
import type { BaseListProps, BasePriceItem, BaseTableList } from 'api-price'
import {
  PriceFundingListCardStyled,
  PriceFundingListCardItemStyled,
  PriceFundingListCardItemFooterStyled,
} from './style'
import useLangPrefix from '@/hooks/useLangPrefix'
import { stopEvent } from '@/utils/tools'
import { useRouter } from 'next/router'

interface FundingListCardProps {
  data: BaseTableList<BasePriceItem> & BaseListProps
}

const FundingListCard: React.FC<FundingListCardProps> = ({ data }): React.ReactElement => {
  const { t } = useTranslation()
  const { langPrefix } = useLangPrefix()
  const { locale } = useRouter()

  const itemOnClick = (item: any) => {
    const href = `${langPrefix}/price/fundraising/${item?.seo_path ?? (`${item?.project_name}`?.replaceAll(' ', '-')?.replaceAll(/[?#<>&]/g, '') + '-' + item?.project_id).toLocaleLowerCase()}`

    location.href = href
  }

  // const { list, totalCount, page, pageSize } = data

  const { formatRateChangePrice } = useRateChange()

  if (!data?.list?.length) {
    return <BaseEmpty />
  }

  return (
    <PriceFundingListCardStyled>
      {data?.list?.map((item: any) => {
        return (
          <PriceFundingListCardItemStyled key={item?.id} onClick={() => itemOnClick(item)}>
            <Flex justify="space-between" align="center" wrap={'wrap'} gap={12}>
              <Flex gap={8} align="center">
                <BaseImage
                  src={item?.logo_url}
                  width={28}
                  height={28}
                  style={{ borderRadius: '50%' }}
                  alt={item?.project_name}
                />
                <span className="font-500 size-16 line-150">{item?.project_name}</span>
              </Flex>

              <Flex
                align="center"
                gap={4}
                wrap={'wrap'}
                onClick={e => {
                  stopEvent(e)
                  if (item?.detail?.slice(2)?.length) {
                    openConfirmModal({
                      styles: {
                        header: {
                          marginBottom: '24px',
                        },
                        body: {
                          '.mantine-Group-root': {
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '24px',
                          },
                          '.mantine-Button-root': {
                            marginTop: '16px',
                            borderRadius: '18px',
                          },
                          '.mantine-Group-root .mantine-UnstyledButton-root:first-of-type': {
                            display: 'none',
                          },
                          '.mantine-Group-root .mantine-UnstyledButton-root:nth-of-type(2)': {
                            width: '100%',
                          },
                        },
                      },
                      radius: 8,
                      padding: 24,
                      centered: true,
                      children: (
                        <Flex gap={12} align="center" wrap={'wrap'} dir={locale === 'ar' ? 'rtl' : undefined}>
                          {item?.detail?.map((item: any) => (
                            <Tag
                              bg="var(--color-cmpt-11)"
                              color="var(--color-text-2)"
                              key={'tag' + item?.key}
                              label={item?.key}
                            />
                          ))}
                        </Flex>
                      ),
                      title: <span className="text-1 size-24 font-500">{t('p.tags', { ns: 'fundraising' })}</span>,
                      labels: { cancel: false, confirm: `${t('p.got.it', { ns: 'fundraising' })}` },
                    })
                  }
                }}
              >
                {item?.detail
                  ?.slice(0, 4)
                  ?.map((item: any) => (
                    <Tag
                      bg="var(--color-cmpt-11)"
                      color="var(--color-text-2)"
                      key={'tag' + item?.key}
                      label={item?.key}
                    />
                  ))}

                {item?.detail?.slice(4)?.length ? (
                  <div
                    onClick={e => {
                      stopEvent(e)
                      openConfirmModal({
                        styles: {
                          header: {
                            marginBottom: '24px',
                          },
                          body: {
                            '.mantine-Group-root': {
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: '24px',
                            },
                            '.mantine-Button-root': {
                              marginTop: '16px',
                              borderRadius: '18px',
                            },
                            '.mantine-Group-root .mantine-UnstyledButton-root:first-of-type': {
                              display: 'none',
                            },
                            '.mantine-Group-root .mantine-UnstyledButton-root:nth-of-type(2)': {
                              width: '100%',
                            },
                          },
                        },
                        radius: 8,
                        padding: 24,
                        centered: true,
                        children: (
                          <Flex gap={12} align="center" wrap={'wrap'} dir={locale === 'ar' ? 'rtl' : undefined}>
                            {item?.detail?.map((item: any) => (
                              <Tag
                                bg="var(--color-cmpt-11)"
                                color="var(--color-text-2)"
                                key={'tag' + item?.key}
                                label={item?.key}
                              />
                            ))}
                          </Flex>
                        ),
                        title: <span className="text-1 size-24 font-500">{t('p.tags', { ns: 'fundraising' })}</span>,
                        labels: { cancel: false, confirm: `${t('p.got.it', { ns: 'fundraising' })}` },
                      })
                    }}
                  >
                    <Tag
                      bg="var(--color-cmpt-15)"
                      color="var(--color-text-5)"
                      label={`+${item?.detail?.slice(4)?.length}`}
                    />
                  </div>
                ) : (
                  ''
                )}
              </Flex>
            </Flex>
            <Flex direction="column" gap={9}>
              <Flex justify="space-between">
                <span className="size-14 font-400 text-3 line-h-21">{t('p.funding', { ns: 'fundraising' })}</span>
                <span className="text-1 font-500 size-16">{item?.rounds_name ?? '--'}</span>
              </Flex>
              <Flex justify="space-between">
                <span className="size-14 font-400 text-3 line-h-21">{t('p.amount', { ns: 'fundraising' })}</span>
                <span className="text-1 size-16 font-500">
                  {formatRateChangePrice(item?.price, { isThousands: true })}
                </span>
              </Flex>
              <Flex justify="space-between">
                <span className="size-14 font-400 text-3 line-h-21">{t('p.valuation', { ns: 'fundraising' })}</span>
                <span className="text-1 size-16 font-500">
                  {formatRateChangePrice(item?.valuation_us ?? '', { isThousands: true })}
                </span>
              </Flex>
              <Flex justify="space-between">
                <span className="size-14 font-400 text-3 line-h-21">{t('p.date', { ns: 'fundraising' })}</span>
                <span className="text-1 font-500 size-16">
                  {item?.publish_date ? dayjs(item?.publish_date).format('YYYY-MM-DD') : '--'}
                </span>
              </Flex>
            </Flex>
            <PriceFundingListCardItemFooterStyled>
              <Action isFundraising={true} isH5Fundraising={true} {...item} />
            </PriceFundingListCardItemFooterStyled>
          </PriceFundingListCardItemStyled>
        )
      })}
    </PriceFundingListCardStyled>
  )
}

export default observer(FundingListCard)
